import { Form, Formik } from 'formik';
import { FC } from 'react';

import { CheckoutDescription } from '@/checkout/components/CheckoutDescription/CheckoutDescription';
import { FinanceResidencyStatusFields } from '@/checkout/components/FinanceResidencyStatusFields/FinanceResidencyStatusFieldsLegacy';
import { employmentStatusOptions } from '@/checkout/templates/Checkout/Forms/CheckoutForm.types';
import { FINANCE_EMPLOYMENT_STATUS, FINANCE_RESIDENCY_STATUS } from '@/data/FinanceApi/FinanceApi.types';
import { MICROCOPY } from '@/data/microcopy/microcopyDictionary';
import { Button } from '@/general/components/Button/Button';
import { InputFormik } from '@/general/components/forms/Input/InputFormik';
import { InputSelectFormik } from '@/general/components/forms/Input/InputSelectFormik';
import { InputTimeFormik } from '@/general/components/forms/Input/InputTimeFormik';
import { getMicrocopy } from '@/stores/microcopyStore';

import styles from './PersonalizedQuoteForm.module.scss';
import {
  EmploymentCitizenshipFormValidation,
  EmploymentCitizenshipFormValues,
  PersonalizedQuoteFormProps,
} from './PersonalizedQuoteForm.type';

const initialValues: EmploymentCitizenshipFormValues = {
  employmentStatus: '' as FINANCE_EMPLOYMENT_STATUS,
  startedEmployerDate: '' as any,
  jobTitle: '',
  totalYearlyIncomeBeforeTax: '' as any,
  residencyStatus: '' as FINANCE_RESIDENCY_STATUS,
  visaSubclassNumber: '' as any,
  visaExpiryDate: '' as any,
  isBusinessUseSelected: false,
};

export const EmploymentCitizenshipForm: FC<PersonalizedQuoteFormProps<EmploymentCitizenshipFormValues>> = ({
  onBack,
  onSubmit,
  values,
}) => {
  return (
    <>
      <CheckoutDescription
        title={
          values.isBusinessUseSelected
            ? getMicrocopy(MICROCOPY.PERSONALIZED_QUOTE_EMPLOYMENT_CITIZENSHIP_FORM_RESIDENCY_STATUS_TITLE)
            : getMicrocopy(MICROCOPY.PERSONALIZED_QUOTE_EMPLOYMENT_CITIZENSHIP_FORM_HEADER)
        }
        onBack={onBack}
      />
      <div className={styles.employmentCitizenship}>
        <Formik
          initialValues={{ ...initialValues, ...values }}
          onSubmit={onSubmit}
          validationSchema={EmploymentCitizenshipFormValidation}
        >
          {(props) => (
            <Form className={styles.form}>
              {!values.isBusinessUseSelected && (
                <>
                  <p className={styles.employmentStatusTitle}>
                    {getMicrocopy(MICROCOPY.PERSONALIZED_QUOTE_EMPLOYMENT_CITIZENSHIP_FORM_EMPLOYMENT_STATUS_TITLE)}
                  </p>
                  <InputSelectFormik
                    name="employmentStatus"
                    id="employmentStatus"
                    label="Employment status"
                    data-testid="employment-status"
                    options={employmentStatusOptions}
                    allowEmpty
                  />
                  <InputTimeFormik
                    label="Date you started at employer"
                    name="startedEmployerDate"
                    id="startedEmployerDate"
                    variant="month"
                    data-testid="started-employer-date"
                  />
                  <div className={styles.container}>
                    <InputFormik id="jobTitle" type="text" name="jobTitle" label="Job title" data-testid="job-title" />
                    <InputFormik
                      id="totalYearlyIncomeBeforeTax"
                      type="numeric"
                      name="totalYearlyIncomeBeforeTax"
                      label="Total yearly income before tax"
                      data-testid="total-yearly-income-before-tax"
                      iconLeft="$"
                    />
                  </div>
                </>
              )}

              <p className={styles.residentialStatusTitle}>
                {getMicrocopy(MICROCOPY.PERSONALIZED_QUOTE_EMPLOYMENT_CITIZENSHIP_FORM_RESIDENCY_STATUS_TITLE)}
              </p>
              <FinanceResidencyStatusFields formikProps={props} />
              <footer className={styles.employmentCitizenshipButtonContainer}>
                <Button type="submit" isLoading={props.isSubmitting} fullWidth data-testid="submit">
                  {getMicrocopy(MICROCOPY.PERSONALIZED_QUOTE_EMPLOYMENT_CITIZENSHIP_FORM_SUBMIT_BUTTON_LABEL)}
                </Button>
              </footer>
            </Form>
          )}
        </Formik>
      </div>
    </>
  );
};
